import React, { useEffect } from "react";
import mapboxgl from "mapbox-gl";
import { useActivity } from "../hooks";
import { LayerEventListener } from "./types";

import US_STATES_DATA, { StatesProperties } from "../data/US_STATES";

interface StatesLayerProps {
   map: mapboxgl.Map;
   states: string[];
}

const SOURCE_STATES_NAME = "source-states";
const LAYER_BORDER_STATES_NAME = "layer-state-borders";
const LAYER_FILLS_STATES_NAME = "layer-state-fills";

const StatesLayer: React.FC<StatesLayerProps> = ({ map, states }) => {
   const { setFilter } = useActivity();

   useEffect(() => {
      map.addSource(SOURCE_STATES_NAME, {
         type: "geojson",
         data: US_STATES_DATA,
      });

      map.addLayer({
         id: LAYER_BORDER_STATES_NAME,
         type: "line",
         source: SOURCE_STATES_NAME,
         paint: {
            "line-color": "#00609C",
            "line-width": 1,
         },
      });

      map.addLayer({
         id: LAYER_FILLS_STATES_NAME,
         type: "fill",
         source: SOURCE_STATES_NAME,
         paint: {
            "fill-color": [
               "case",
               ["boolean", ["feature-state", "hover"], false],
               "#00609C",
               "#A1DED2",
            ],
            "fill-opacity": 0.2,
         },
      });

      const mouseMoveListener = () => {
         map.getCanvas().style.cursor = "pointer";
      };

      const mouseLeaveListener = () => {
         map.getCanvas().style.cursor = "";
      };

      const clickListener = (e: LayerEventListener<"click">) => {
         if (e.features) {
            const { state } = e.features[0].properties as StatesProperties;
            setFilter((filter) => ({
               state: filter.state ? undefined : state,
            }));
         }
      };

      map.on("mouseenter", LAYER_FILLS_STATES_NAME, mouseMoveListener);
      map.on("mouseleave", LAYER_FILLS_STATES_NAME, mouseLeaveListener);
      map.on("click", LAYER_FILLS_STATES_NAME, clickListener);

      return () => {
         map.off("mouseenter", LAYER_FILLS_STATES_NAME, mouseMoveListener);
         map.off("mouseleave", LAYER_FILLS_STATES_NAME, mouseLeaveListener);
         map.off("click", LAYER_FILLS_STATES_NAME, clickListener);
         map.removeLayer(LAYER_BORDER_STATES_NAME);
         map.removeLayer(LAYER_FILLS_STATES_NAME);
         map.removeSource(SOURCE_STATES_NAME);
      };
   }, [map, setFilter]);

   useEffect(() => {
      map.setFilter(LAYER_BORDER_STATES_NAME, [
         "match",
         ["get", "state"],
         [...states, ""],
         true,
         false,
      ]);

      map.setFilter(LAYER_FILLS_STATES_NAME, [
         "match",
         ["get", "state"],
         [...states, ""],
         true,
         false,
      ]);
   }, [map, states]);

   return null;
};

export default StatesLayer;
